import React from 'react';
import { SectionHeading, DetailRow, PasswordField } from './primitives';
import { passwordStrength } from './profileConstants';

interface SecurityCardProps {
  lastPasswordChange: string;
  formatDate: (iso: string) => string;
  currentPassword: string;
  setCurrentPassword: (v: string) => void;
  newPassword: string;
  setNewPassword: (v: string) => void;
  confirmPassword: string;
  setConfirmPassword: (v: string) => void;
  passwordError: string;
  passwordSuccess: string;
  changingPassword: boolean;
  confirmMismatch: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const SecurityCard: React.FC<SecurityCardProps> = ({
  lastPasswordChange,
  formatDate,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  passwordError,
  passwordSuccess,
  changingPassword,
  confirmMismatch,
  onSubmit,
}) => {
  const strength = passwordStrength(newPassword);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 dark:border-gray-700 dark:bg-gray-800">
      <SectionHeading
        icon={
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H6.75a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z" />
          </svg>
        }
      >
        Security &amp; Access
      </SectionHeading>

      <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2">
        {/* Left: status */}
        <dl className="lg:max-w-sm">
          <DetailRow label="Password">••••••••</DetailRow>
          <DetailRow label="Last Changed">
            {lastPasswordChange ? formatDate(lastPasswordChange) : '—'}
          </DetailRow>
        </dl>

        {/* Right: change form */}
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Change password</p>
          <PasswordField
            id="profile-current-pw"
            label="Current Password"
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
          />
          <PasswordField
            id="profile-new-pw"
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            minLength={6}
            hint={
              newPassword ? (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map(i => (
                      <span
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i < Math.min(strength.score, 4) ? strength.color : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Password strength: <span className="font-medium">{strength.label}</span>
                    <span className="text-gray-400 dark:text-gray-500"> · use 8+ chars with a number &amp; symbol</span>
                  </p>
                </div>
              ) : undefined
            }
          />
          <PasswordField
            id="profile-confirm-pw"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            minLength={6}
            hint={
              confirmMismatch ? (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords do not match</p>
              ) : confirmPassword && !confirmMismatch ? (
                <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd" d="M16.7 6.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 12.6l6.3-6.3a1 1 0 011.4 0z" clipRule="evenodd" />
                  </svg>
                  Passwords match
                </p>
              ) : undefined
            }
          />

          {passwordError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/30 dark:text-red-400">
              {passwordError}
            </p>
          )}
          {passwordSuccess && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              {passwordSuccess}
            </p>
          )}

          <button
            type="submit"
            disabled={changingPassword || !currentPassword || !newPassword || confirmMismatch}
            className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {changingPassword ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SecurityCard;
